import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserManager } from "oidc-client-ts";
import { oidcConfig } from "../config/oidcConfig";


const OAuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Authenticating...");
  const [error, setError] = useState(null);
  const [needsLocalLogin, setNeedsLocalLogin] = useState(false);
  const [localUsername, setLocalUsername] = useState("");
  const [localPassword, setLocalPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    try {
      const currentUrl = window.location.href;
      console.debug("OAuth callback URL:", currentUrl);
      setStatus("Exchanging code for tokens...");

      const userManager = new UserManager(oidcConfig);

      const timeout = setTimeout(() => {
        setError("Timeout while exchanging authorization code.");
      }, 15000);

      userManager.signinRedirectCallback(currentUrl).then(async (user) => {
        clearTimeout(timeout);
        if (user && user.id_token) {
          sessionStorage.setItem("id_token", user.id_token);
          sessionStorage.setItem("access_token", user.access_token);
          localStorage.setItem("username", user.profile.preferred_username);
          // Prefill local username for finalize step if needed
          setLocalUsername(user.profile.preferred_username || "");
          // If credentials were captured before redirect, call existing login API
          try {
            const apiBase = process.env.REACT_APP_API_URL;
            const u = sessionStorage.getItem("pending_login_username");
            const p = sessionStorage.getItem("pending_login_password");

            if (apiBase && u && p) {
              setStatus("Finalizing session...");
              const loginUrl = `${apiBase}/api/admin/login/`;
              const resp = await fetch(loginUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: u, password: p }),
              });
              if (resp.ok) {
                const data = await resp.json();
                if (data?.token) localStorage.setItem("token", data.token);
                if (data?.jobs !== undefined) localStorage.setItem("jobs", data.jobs);
                if (data?.teams !== undefined) localStorage.setItem("teams", data.teams);
                if (data?.additional_message !== undefined) localStorage.setItem("additional_message", data.additional_message);
                if (data?.access_permissions) localStorage.setItem("access_permissions", JSON.stringify(data.access_permissions));
                if (data?.profile_picture) localStorage.setItem("profile_picture", data.profile_picture);
                if (data?.data) {
                  const d = data.data;
                  if (d.id !== undefined) localStorage.setItem("id", d.id);
                  if (d.role_id !== undefined) localStorage.setItem("role_id", d.role_id);
                  if (d.jobs_id !== undefined) localStorage.setItem("jobs_id", d.jobs_id);
                  if (d.team_id !== undefined) localStorage.setItem("team_id", d.team_id);
                  if (d.company_id !== undefined) localStorage.setItem("company_id", d.company_id);
                }
                localStorage.setItem("login", true);
                localStorage.setItem("username", u);

                sessionStorage.removeItem("pending_login_username");
                sessionStorage.removeItem("pending_login_password");
                // Only navigate to dashboard after legacy login succeeds
                navigate("/dashboard");
                return;
              } else {
                console.warn("Legacy login API failed after SSO:", resp.status);
                setError("Login API failed after SSO. Please try again.");
                return;
              }
            }
          } catch (legacyErr) {
            console.warn("Legacy login API error after SSO:", legacyErr);
            setError("Login API error after SSO. Please try again.");
            return;
          }

          // If no local creds, ask user to finalize with existing login API (no backend change)
          setStatus("Authenticated via SSO. Please finalize login.");
          setNeedsLocalLogin(true);
          return;
        } else {
          setError("No tokens returned by identity provider.");
        }
      }).catch((e) => {
        console.error("OIDC callback error:", e);
        setError(e?.message || "Authentication failed");
      });
    } catch (e) {
      console.error("Unexpected callback error:", e);
      setError(e?.message || "Authentication failed");
    }
  }, [navigate]);

  const handleFinalizeLogin = async (e) => {
    e.preventDefault();
    try {
      const apiBase = process.env.REACT_APP_API_URL;
      if (!apiBase) {
        setError("API base URL is not configured.");
        return;
      }
      if (!localUsername || !localPassword) return;
      setIsSubmitting(true);
      setStatus("Finalizing session...");
      const loginUrl = `${apiBase}/api/admin/login/`;
      const resp = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: localUsername, password: localPassword }),
      });
      if (!resp.ok) {
        setIsSubmitting(false);
        setError("Invalid username or password.");
        return;
      }
      const data = await resp.json();
      if (data?.token) localStorage.setItem("token", data.token);
      if (data?.jobs !== undefined) localStorage.setItem("jobs", data.jobs);
      if (data?.teams !== undefined) localStorage.setItem("teams", data.teams);
      if (data?.additional_message !== undefined) localStorage.setItem("additional_message", data.additional_message);
      if (data?.access_permissions) localStorage.setItem("access_permissions", JSON.stringify(data.access_permissions));
      if (data?.profile_picture) localStorage.setItem("profile_picture", data.profile_picture);
      if (data?.data) {
        const d = data.data;
        if (d.id !== undefined) localStorage.setItem("id", d.id);
        if (d.role_id !== undefined) localStorage.setItem("role_id", d.role_id);
        if (d.jobs_id !== undefined) localStorage.setItem("jobs_id", d.jobs_id);
        if (d.team_id !== undefined) localStorage.setItem("team_id", d.team_id);
        if (d.company_id !== undefined) localStorage.setItem("company_id", d.company_id);
      }
      localStorage.setItem("login", true);
      localStorage.setItem("username", localUsername);
      setIsSubmitting(false);
      navigate("/dashboard");
    } catch (err) {
      console.error("Finalize login error:", err);
      setIsSubmitting(false);
      setError("Login API error. Please try again.");
    }
  };

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">Login Error</div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button onClick={() => navigate("/")} className="px-4 py-2 bg-blue-600 text-white rounded">Back to login</button>
        </div>
      </div>
    );
  }

  if (needsLocalLogin) {
    return (
      <div className="h-screen flex items-center justify-center">
        <form onSubmit={handleFinalizeLogin} className="w-full max-w-sm bg-white shadow p-6 rounded">
          <div className="text-lg font-medium mb-4">Finalize login</div>
          <label className="block text-sm mb-1">Username</label>
          <input value={localUsername} onChange={(e) => setLocalUsername(e.target.value)} className="w-full border rounded px-3 py-2 mb-3" placeholder="Username" />
          <label className="block text-sm mb-1">Password</label>
          <input type="password" value={localPassword} onChange={(e) => setLocalPassword(e.target.value)} className="w-full border rounded px-3 py-2 mb-4" placeholder="Password" />
          <button disabled={isSubmitting || !localUsername || !localPassword} className="w-full bg-blue-600 text-white rounded px-4 py-2">
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return <div>{status}</div>;
};

export default OAuthCallback;